import FieldValidationError from '@viking-eng/field-validation-error';
import PropTypes from '@viking-eng/prop-types';
import Spinner from '@viking-eng/spinner';
import { requiredLabel } from '@viking-eng/utils';
import classNames from 'classnames';
import React from 'react';
import './InputText.css';

// meta: state of this field that redux-form tracking
// input: Redux form input prop
// inputAttributes: Additional input props
// placeholder: Custom Placeholder Value
// placeholderClass: Label Name
// errorCodes: Errorcodes (Required/Invalid)
// errorMessages: Error Messages from Redux
// classes: Custom text area classes
// type: text/email
// freshAddressValid: Boolean value for validating email address

function InputText({
  classes,
  dataId,
  errorCode,
  errorCodes,
  errorMessages,
  freshAddressValid,
  input,
  inputAttributes,
  isActive,
  isErrorVisible,
  isLoading,
  isWarningVisible,
  maxLength,
  meta,
  placeholder,
  required,
  tabIndex,
  title,
  type,
  validationErrorTabIndex,
  warningCode,
}) {
  placeholder = placeholder || input.placeholder || inputAttributes.placeholder || title;
  const isErrorDisplayed =
    isErrorVisible ||
    (meta.touched && !meta.active && (meta.error || !freshAddressValid) && !meta.asyncValidating && !meta.warning);
  const isWarningDisplayed =
    isWarningVisible || (meta.touched && !meta.active && (meta.warning || !freshAddressValid) && !meta.asyncValidating);

  return (
    <div
      className={classNames('input-text-section', {
        'error-snap': meta.error || !!isErrorDisplayed,
      })}
      data-id={dataId}
    >
      <div
        className={classNames('form-group', {
          'validation-error': isErrorDisplayed,
        })}
        title={title}
      >
        <input
          {...input}
          {...inputAttributes}
          className={classNames('form-control', `${classes}`, {
            highlight: meta.active || isActive,
            'validation-error': isErrorDisplayed,
            'with-placeholder': placeholder,
            required: required && !input.value,
          })}
          maxLength={maxLength}
          type={type}
          tabIndex={tabIndex}
        />
        {placeholder && (
          <span
            className={classNames('form-control-placeholder', {
              'form-label': !!input.value,
              'validation-error': isErrorDisplayed,
              required: required && !input.value,
            })}
          >
            {required ? requiredLabel(placeholder) : placeholder}
          </span>
        )}
        {!isErrorDisplayed && isLoading && (
          <div className="input-text-spinner">
            <Spinner />
          </div>
        )}
        {isErrorDisplayed && (
          <FieldValidationError
            errorCode={meta.error || errorCode}
            errorCodes={errorCodes}
            errorMessages={errorMessages}
            tabIndex={validationErrorTabIndex}
          />
        )}
        {isWarningDisplayed && (
          <FieldValidationError
            errorCode={meta.warning || warningCode}
            errorCodes={errorCodes}
            errorMessages={errorMessages}
            tabIndex={validationErrorTabIndex}
          />
        )}
      </div>
    </div>
  );
}

InputText.defaultProps = {
  classes: null,
  dataId: undefined,
  errorCodes: {},
  errorMessages: {},
  freshAddressValid: true,
  input: {},
  inputAttributes: {},
  maxLength: null,
  meta: {},
  placeholder: null,
  required: null,
  tabIndex: '',
  title: '',
  type: 'text',
  validationErrorTabIndex: 0,
};

InputText.propTypes = {
  classes: PropTypes.string,
  dataId: PropTypes.string,
  errorCode: PropTypes.string,
  errorCodes: PropTypes.shape({}),
  errorMessages: PropTypes.errorMessages,
  freshAddressValid: PropTypes.bool,
  input: PropTypes.shape({
    value: PropTypes.string,
    placeholder: PropTypes.string,
    name: PropTypes.string,
    onBlur: PropTypes.func,
    onChange: PropTypes.func,
    onDragStart: PropTypes.func,
    onDrop: PropTypes.func,
    onFocus: PropTypes.func,
  }),
  inputAttributes: PropTypes.shape({
    'aria-label': PropTypes.string,
    'aria-required': PropTypes.bool,
    alt: PropTypes.string,
    id: PropTypes.string,
    disabled: PropTypes.bool,
    placeholder: PropTypes.string,
    inputMode: PropTypes.string,
  }),
  isActive: PropTypes.bool,
  isErrorVisible: PropTypes.bool,
  isWarningVisible: PropTypes.bool,
  isLoading: PropTypes.bool,
  maxLength: PropTypes.number,
  meta: PropTypes.reduxFormMeta,
  placeholder: PropTypes.string,
  required: PropTypes.bool,
  tabIndex: PropTypes.string,
  title: PropTypes.string,
  type: PropTypes.string,
  validationErrorTabIndex: PropTypes.number,
  warningCode: PropTypes.string,
};

export default InputText;
